/* ManageDashboardsComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageDashboardsRenderer");
const defaultDashboardImage = require("./defaultDashboardImage");

class ManageRolesComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userService,
        userSession,
        logger,
        DashboardPref,
        MUF,
        UiUtils
    ) {
        super();
        this._OrganizationService = OrganizationService;
        this._organizationData = {};
        this._userService = userService;
        this._userSession = userSession;
        this._dashboardPrefEntityDef = DashboardPref;
        this._myUserInfo = undefined;
        this._logger = logger;
        this._dashboardPrefEntity = undefined;
        this._modalUserFeedback = MUF;
        this._defaultDashboardImage = defaultDashboardImage;
        this._hasDashboardManageAccess = false;
        this._tempPatches = false;
        this._uiUtils = UiUtils;

        // this._getUserInfoProm = this.fetchUserInfo();
        this._activeOrgId = _.get(userSession, "activeOrganization.id");
        this._defaultDashboardPrefs = { 
            allowDisableSessionTimeout: false,
            // defaultTheme: "LIGHT", 
            // enableFavoriteHotkeys: true, 
            // enableFavoriteShortcuts: true, 
            enableImageUpload: true,
            enable: false,
            defaultImage: this._defaultDashboardImage
        };

        this._EnableDisableDashboard = this._getEnableDisableDashboardConfig();

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {
        this._dashboardPrefEntity.on("DirtyStateChanged", this._onDirtyStateChanged.bind(this));
    }
    _unregisterListeners() {
        this._dashboardPrefEntity.removeAllListeners("DirtyStateChanged");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get Dirty() {
        return this._dashboardPrefEntity.isDirty();
    }

    get userInfo() {
        return this.getUserInfo();
    }
    getUserInfo() {
        return this._myUserInfo;
    }

    get slickData() {
        return this.getSlickData();
    }

    getSlickData() {
        return this._slickFormatData;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return this._activeOrgId;
    }

    shutdown() {
        this._unregisterListeners();
        
        return this._renderer
            ._shutdown()
            .then(
                () => {
                    this._renderer = undefined;
                    this._activeOrgId = undefined;
                    this._userService = undefined;
                    this._userSession = undefined;
                    this._myUserInfo = undefined;
                    this._logger = undefined;
                    this._dashboardPrefEntity = undefined;
                    this._dashboardPrefEntityDef = undefined;
                    this._EnableDisableDashboard = undefined;
                    this._tempPatches = undefined;
                    this._uiUtils = undefined;

                    this._OrganizationService = undefined;
                    this._organizationData = undefined;
                },
                (err) => {
                    this._logger.warn(
                        { error: err },
                        "ManageOrgsComponent.shutdown() Error shutting down ManageOrgsComponent"
                    );
                }
            );
    }

    whenModelIsReady() {
        this._hasDashboardManageAccess = this._userSession.user.authorizedActions.includes("dashboardPrefsWrite");
        return Promise.resolve().then(() => {
            this.loadOrganizationData();
            return this.retrieveDashboardPref();
            // return Promise.resolve();
        }).then(() => {
            if(this.activeOrgId !== this._dashboardPrefEntity.getTenantId()) {
                return this.retrieveLocalDashboardPref();
            }
            return Promise.resolve();
        });
    }

    retrieveDashboardPref() {
        return this._dashboardPrefEntityDef.retrieveAscendDashboardPrefs(this.activeOrgId).then((dashboardPrefEntity) => {
            this._dashboardPrefEntity = dashboardPrefEntity;
            this._registerListeners();
            return Promise.resolve();
        });
    }

    retrieveLocalDashboardPref() {
        return this._dashboardPrefEntityDef.retrieveDashboardPrefs(this.activeOrgId).then((dashboardPrefEntity) => {
            this._unregisterListeners();
            this._defaultDashboardImage = this._dashboardPrefEntity.getDefaultImage();
            
            dashboardPrefEntity.setDefaultImage(this._dashboardPrefEntity.getDefaultImage());
            dashboardPrefEntity.setEnableImageUpload(this._dashboardPrefEntity.getEnableImageUpload());
            this._tempPatches = true;
            this._dashboardPrefEntity = dashboardPrefEntity;
            this._registerListeners();
            return Promise.resolve();
        });
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _getEnableDisableDashboardConfig() {
        if(this._userSession && this._userSession._userEntity) {
            return !!_.get(this._userSession._userEntity.authorizedTenants, `${this.activeOrgId}.providerName`, false);
        }
    }

    saveDashboardPrefs() {
        if(this._dashboardPrefEntity.isDirty()) {
           return this._dashboardPrefEntity.update();
        }
        return Promise.resolve();
    }

    clearPatches() {
        this._dashboardPrefEntity.clearPendingPatches();
        this.emit("PatchesCleared");
    }

    loadOrganizationData() {
        return this._OrganizationService.getById({ id: this.activeOrgId }).then((orgData) => {
            this._organizationData = orgData;
            return orgData;
        });
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    _onDirtyStateChanged(state) {
        this.emit("Dirty", state);
    }

    _triggerOrganizationDataChanged(orgData) {
        this.emit("OrganizationDataChange", orgData);
    }

    organizationChanged({ id, name }) {
        if (this.activeOrgId === id) {
            return;
        }
        this._activeOrgId = id;
        this._triggerOrganization({ id, name });

        this._unregisterListeners();
        return this.whenModelIsReady().then(() => {
            this._EnableDisableDashboard = this._getEnableDisableDashboardConfig();
            this._triggerDataUpdated();
        });
    }

    _triggerOrganization(org) {
        /**
         * @event OSManagementComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit("Organization", org);
    }

    _triggerDataUpdated() {
        /**
         * @event OSManagementComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = ManageRolesComponent;
ManageRolesComponent.$groupName = "Organization";
ManageRolesComponent.$displayName = "Dashboards";
ManageRolesComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.services.instance.User",
    "$teve.userSession",
    "$teve.logger",
    "$teve.entities.DashboardPrefs",
    "$teve.ui.userFeedback",
    "$teve.ui.UiUtils"
];
